import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { Users, Sword, Shield, ChevronLeft, Crown, Skull, Droplets, Ghost, Star, MapPin, Zap, DollarSign } from 'lucide-react';
import 'leaflet/dist/leaflet.css';

const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

// NYC center coordinates
const NYC_CENTER = [38.5, -95.0]; // US center for wider view
const NYC_ZOOM = 4; // Start zoomed out to show all regions

// Ownership color logic
const getBlockColors = (territory, myGameId) => {
  if (!territory.owner_id) return { fill: '#3a3a3a', stroke: '#555', label: 'NEUTRAL' };
  if (territory.owner_id === myGameId) return { fill: '#C9A84C', stroke: '#FFD700', label: 'YOURS' };
  return { fill: '#8B1A1A', stroke: '#cc3333', label: 'ENEMY' };
};

// Story marker icons
const MARKER_LABELS = {
  red_skull: { icon: <Skull size={11} />, color: '#ff4444', label: 'Blood was spilled here' },
  gold_star: { icon: <Star size={11} />, color: '#FFD700', label: 'Major canon event' },
  water_drop: { icon: <Droplets size={11} />, color: '#4A9BD9', label: 'Water moment' },
  ghost: { icon: <Ghost size={11} />, color: '#E8E0D0', label: "Rosa's territory" },
  crown: { icon: <Crown size={11} />, color: '#FFD700', label: 'Power location' },
};

// Borough colors for the filter pills
const BOROUGH_PILL_COLORS = {
  manhattan: '#C9A84C',
  brooklyn: '#CD7F32',
  queens: '#4A7AB5',
  bronx: '#9B59B6',
  staten_island: '#27AE60',
  new_jersey: '#3498DB',
  waterfront_district: '#FFD700',
};

// Radius based on income (higher income = bigger dot)
const getRadius = (income, zoom) => {
  const base = Math.max(4, Math.min(14, Math.sqrt(income / 200)));
  const zoomScale = zoom > 13 ? 1.5 : zoom > 12 ? 1.2 : 1;
  return base * zoomScale;
};

// =============================================
// MAP ZOOM TRACKER
// =============================================
const ZoomTracker = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
  });
  return null;
};

// =============================================
// FLY TO COMPONENT
// =============================================
const FlyTo = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom, { duration: 0.8 });
  }, [center, zoom, map]);
  return null;
};

// =============================================
// STORY EVENT OVERLAY
// =============================================
const StoryEventOverlay = ({ event, onClose }) => {
  if (!event) return null;
  const marker = MARKER_LABELS[event.marker] || MARKER_LABELS.water_drop;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-6"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="max-w-md text-center"
        onClick={e => e.stopPropagation()}
      >
        <div className="mb-4" style={{ color: marker.color }}>
          {React.cloneElement(marker.icon, { size: 40, className: 'mx-auto' })}
        </div>
        <h3 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#C9A84C] mb-4">
          {event.territory_name}
        </h3>
        <div className="w-16 h-px bg-[#C9A84C]/30 mx-auto mb-4" />
        <p className="font-['Cormorant_Garamond'] text-lg italic text-[#E8E0D0]/70 leading-relaxed mb-6">
          "{event.narration}"
        </p>
        <p className="font-['Outfit'] text-[#E8E0D0]/20 text-[9px] tracking-widest mb-4">-- THE WATER</p>
        <button onClick={onClose}
          className="px-6 py-2 bg-[#C9A84C]/10 border border-[#C9A84C]/30 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20">
          CONTINUE
        </button>
      </motion.div>
    </motion.div>
  );
};

// =============================================
// TERRITORY DETAIL PANEL (Bottom Sheet)
// =============================================
const TerritoryDetail = ({ territory, myGameId, onClaim, onAttack, onReinforce, onClose, loading }) => {
  if (!territory) return null;

  const isOwned = territory.owner_id === myGameId;
  const isNeutral = !territory.owner_id;
  const isEnemy = territory.owner_id && territory.owner_id !== myGameId;
  const colors = getBlockColors(territory, myGameId);
  const marker = territory.story_marker ? MARKER_LABELS[territory.story_marker] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 30 }}
      className="absolute bottom-0 left-0 right-0 bg-[#0a0c10]/95 backdrop-blur-md border-t border-[#1A2535] p-4 z-[1000] rounded-t-xl"
      style={{ maxHeight: '45%', overflowY: 'auto' }}
    >
      <div className="max-w-lg mx-auto space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#E8E0D0]">{territory.kow_name}</h4>
              {marker && <span style={{ color: marker.color }}>{marker.icon}</span>}
              {territory.premium && <Crown size={14} className="text-[#FFD700]" />}
            </div>
            <p className="font-['Outfit'] text-[#E8E0D0]/30 text-[10px]">{territory.name}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-['Outfit'] text-[8px] px-1.5 py-0.5 rounded border text-[#E8E0D0]/40 border-[#1A2535]">
                {territory.borough?.replace(/_/g, ' ').toUpperCase()}
              </span>
              <span className="font-['Outfit'] text-[9px] text-[#E8E0D0]/20">ERA {territory.era}</span>
              <span className="font-['Outfit'] text-[9px] px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.fill + '30', color: colors.fill, border: `1px solid ${colors.fill}40` }}>
                {colors.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="text-[#E8E0D0]/20 hover:text-[#E8E0D0] transition-colors p-1" data-testid="territory-close">
            <ChevronLeft size={18} />
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">INCOME</p>
            <p className="font-['Outfit'] text-sm text-[#4ADE80] font-bold">${territory.income_per_cycle?.toLocaleString()}</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">DEFENSE</p>
            <p className="font-['Outfit'] text-sm text-[#C9A84C] font-bold">{territory.defense_level}/10</p>
          </div>
          <div className="bg-[#111820] rounded-lg p-2 text-center">
            <p className="font-['Outfit'] text-[7px] text-[#E8E0D0]/20 tracking-widest">CAPTURES</p>
            <p className="font-['Outfit'] text-sm text-[#E8E0D0] font-bold">{territory.capture_count || 0}</p>
          </div>
        </div>

        {/* Owner info */}
        {territory.owner_name && (
          <div className="bg-[#111820] rounded-lg px-3 py-2">
            <span className="font-['Outfit'] text-[#E8E0D0]/20 text-[8px]">CONTROLLED BY: </span>
            <span className="font-['Outfit'] text-[#E8E0D0] text-xs">{territory.owner_name}</span>
          </div>
        )}

        {/* Special ability */}
        {territory.special && (
          <div className="bg-[#C9A84C]/5 border border-[#C9A84C]/15 rounded-lg px-3 py-2">
            <p className="font-['Outfit'] text-[9px] text-[#C9A84C]/80">{territory.special}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          {isNeutral && (
            <button onClick={() => onClaim(territory.territory_id)} disabled={loading}
              className="flex-1 py-2.5 bg-[#C9A84C]/10 border border-[#C9A84C]/40 rounded-lg text-[#C9A84C] font-['Outfit'] text-xs tracking-widest hover:bg-[#C9A84C]/20 disabled:opacity-30"
              data-testid="territory-claim-btn">
              {loading ? '...' : `CLAIM ($${(territory.income_per_cycle * 2)?.toLocaleString()})`}
            </button>
          )}
          {isEnemy && (
            <button onClick={() => onAttack(territory.territory_id)} disabled={loading}
              className="flex-1 py-2.5 bg-[#8B1A1A]/10 border border-[#8B1A1A]/40 rounded-lg text-[#8B1A1A] font-['Outfit'] text-xs tracking-widest hover:bg-[#8B1A1A]/20 disabled:opacity-30"
              data-testid="territory-attack-btn">
              {loading ? '...' : 'ATTACK (10 ENERGY)'}
            </button>
          )}
          {isOwned && (
            <button onClick={() => onReinforce(territory.territory_id)} disabled={loading || territory.defense_level >= 10}
              className="flex-1 py-2.5 bg-[#4ADE80]/10 border border-[#4ADE80]/30 rounded-lg text-[#4ADE80] font-['Outfit'] text-xs tracking-widest hover:bg-[#4ADE80]/20 disabled:opacity-30"
              data-testid="territory-reinforce-btn">
              {loading ? '...' : `REINFORCE ($${(territory.defense_level * 2000)?.toLocaleString()})`}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// =============================================
// MAIN MULTIPLAYER MAP COMPONENT
// =============================================
const MultiplayerMap = ({ gameId, onBack }) => {
  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTerritory, setSelectedTerritory] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [storyEvent, setStoryEvent] = useState(null);
  const [filter, setFilter] = useState('all');
  const [viewBorough, setViewBorough] = useState(null);
  const [warFeed, setWarFeed] = useState([]);
  const [zoom, setZoom] = useState(NYC_ZOOM);
  const [flyTarget, setFlyTarget] = useState(null);

  // Borough center coordinates for flyTo
  const BOROUGH_CENTERS = {
    brooklyn: [40.6501, -73.9496],
    manhattan: [40.7580, -73.9855],
    bronx: [40.8448, -73.8648],
    queens: [40.7282, -73.7949],
    staten_island: [40.5795, -74.1502],
    new_jersey: [40.7440, -74.0324],
    waterfront_district: [40.7180, -73.9680],
    atlanta: [33.7490, -84.3880],
    los_angeles: [33.9425, -118.2551],
    texas: [30.5000, -97.5000],
    connecticut: [41.3083, -72.9279],
    virginia: [37.5407, -77.4360],
    nebraska: [41.2565, -95.9345],
    colombia: [6.2518, -75.5636],
    mexico: [19.4326, -99.1332],
  };

  const loadMap = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/multiplayer/map`);
      if (res.ok) setMapData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadFeed = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/multiplayer/feed?limit=8`);
      if (res.ok) {
        const data = await res.json();
        setWarFeed(data.events || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  // Heartbeat
  useEffect(() => {
    if (!gameId) return;
    const beat = () => fetch(`${API}/api/multiplayer/presence/heartbeat?game_id=${gameId}`, { method: 'POST' }).catch(() => {});
    beat();
    const interval = setInterval(beat, 30000);
    return () => clearInterval(interval);
  }, [gameId]);

  useEffect(() => { loadMap(); loadFeed(); }, [loadMap, loadFeed]);
  useEffect(() => { const i = setInterval(() => { loadMap(); loadFeed(); }, 15000); return () => clearInterval(i); }, [loadMap, loadFeed]);

  const doAction = async (url, body, successMsg) => {
    setActionLoading(true);
    try {
      const res = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok && (data.success || data.won !== undefined)) {
        setActionResult({ type: data.won === false ? 'fail' : 'success', msg: data.narrator || successMsg });
        if (data.story_event) setStoryEvent(data.story_event);
        setSelectedTerritory(null);
        loadMap();
        loadFeed();
      } else {
        setActionResult({ type: 'error', msg: data.detail || 'Failed' });
      }
    } catch (e) { setActionResult({ type: 'error', msg: 'Network error' }); }
    setActionLoading(false);
    setTimeout(() => setActionResult(null), 4000);
  };

  const handleClaim = (tid) => doAction(`${API}/api/multiplayer/territory/claim`, { game_id: gameId, territory_id: tid });
  const handleAttack = (tid) => doAction(`${API}/api/multiplayer/territory/attack`, { game_id: gameId, territory_id: tid });
  const handleReinforce = (tid) => doAction(`${API}/api/multiplayer/territory/reinforce`, { game_id: gameId, territory_id: tid });

  const handleBoroughClick = (borough) => {
    if (viewBorough === borough) {
      setViewBorough(null);
      setFlyTarget({ center: NYC_CENTER, zoom: NYC_ZOOM });
    } else {
      setViewBorough(borough);
      const nycBoroughs = ['brooklyn', 'manhattan', 'bronx', 'queens', 'staten_island', 'new_jersey', 'waterfront_district'];
      const zoomLevel = nycBoroughs.includes(borough) ? 13 : 10;
      setFlyTarget({ center: BOROUGH_CENTERS[borough] || NYC_CENTER, zoom: zoomLevel });
    }
  };

  if (loading) return <p className="font-['Outfit'] text-[#E8E0D0]/30 text-xs text-center py-12">Loading map...</p>;
  if (!mapData) return null;

  const territories = mapData.territories || [];

  // Filter
  let filtered = territories;
  if (viewBorough) filtered = filtered.filter(t => t.borough === viewBorough);
  if (filter === 'mine') filtered = filtered.filter(t => t.owner_id === gameId);
  else if (filter === 'enemy') filtered = filtered.filter(t => t.owner_id && t.owner_id !== gameId);
  else if (filter === 'neutral') filtered = filtered.filter(t => !t.owner_id);
  else if (filter === 'story') filtered = filtered.filter(t => t.story_marker);

  const myTerritories = territories.filter(t => t.owner_id === gameId);
  const myIncome = myTerritories.reduce((s, t) => s + (t.income_per_cycle || 0), 0);

  const boroughs = ['brooklyn', 'manhattan', 'bronx', 'queens', 'staten_island', 'new_jersey', 'waterfront_district', 'atlanta', 'los_angeles', 'texas', 'connecticut', 'virginia', 'nebraska', 'colombia', 'mexico'];

  return (
    <div className="relative" style={{ height: '600px' }} data-testid="multiplayer-map">
      {/* Story overlay */}
      <AnimatePresence>
        {storyEvent && <StoryEventOverlay event={storyEvent} onClose={() => setStoryEvent(null)} />}
      </AnimatePresence>

      {/* TOP BAR — floating over map */}
      <div className="absolute top-0 left-0 right-0 z-[1000] pointer-events-none">
        <div className="p-2 flex items-center justify-between pointer-events-auto">
          <button onClick={onBack}
            className="px-3 py-1.5 bg-[#0a0c10]/80 backdrop-blur-sm border border-[#1A2535] rounded-lg font-['Outfit'] text-[#E8E0D0]/50 text-[9px] tracking-widest hover:text-[#C9A84C] transition-colors"
            data-testid="mp-back">
            BACK
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-[#0a0c10]/80 backdrop-blur-sm border border-[#1A2535] rounded-lg">
            <div className="flex items-center gap-1">
              <div className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] animate-pulse" />
              <span className="font-['Outfit'] text-[#4ADE80] text-[9px]">{mapData.online_players} online</span>
            </div>
            <span className="text-[#1A2535]">|</span>
            <span className="font-['Outfit'] text-[#C9A84C] text-[9px]">{myTerritories.length} blocks</span>
            <span className="text-[#1A2535]">|</span>
            <span className="font-['Outfit'] text-[#4ADE80] text-[9px]">${myIncome.toLocaleString()}/cycle</span>
          </div>
        </div>

        {/* Borough pills */}
        <div className="px-2 flex gap-1 overflow-x-auto pb-1 pointer-events-auto scrollbar-hide">
          <button onClick={() => { setViewBorough(null); setFlyTarget({ center: NYC_CENTER, zoom: NYC_ZOOM }); }}
            className={`shrink-0 px-2 py-1 rounded-full font-['Outfit'] text-[8px] tracking-wider transition-all ${!viewBorough ? 'bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]/40' : 'bg-[#0a0c10]/80 text-[#E8E0D0]/40 border border-[#1A2535]'}`}>
            ALL ({mapData.total_blocks})
          </button>
          {boroughs.map(b => {
            const c = BOROUGH_PILL_COLORS[b] || '#C9A84C';
            const count = mapData.borough_counts?.[b] || 0;
            const active = viewBorough === b;
            return (
              <button key={b} onClick={() => handleBoroughClick(b)}
                className={`shrink-0 px-2 py-1 rounded-full font-['Outfit'] text-[8px] tracking-wider transition-all border backdrop-blur-sm ${active ? '' : 'bg-[#0a0c10]/80 border-[#1A2535]'}`}
                style={active ? { backgroundColor: c + '25', color: c, borderColor: c + '50' } : { color: '#E8E0D050' }}>
                {b === 'waterfront_district' ? 'WF DIST' : b.replace(/_/g, ' ').toUpperCase()} ({count})
              </button>
            );
          })}
        </div>

        {/* Filter row */}
        <div className="px-2 mt-1 flex gap-1 pointer-events-auto">
          {['all', 'mine', 'enemy', 'neutral', 'story'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-2 py-0.5 rounded-full font-['Outfit'] text-[7px] tracking-widest transition-all ${
                filter === f ? 'bg-[#C9A84C]/15 text-[#C9A84C] border border-[#C9A84C]/30' : 'bg-[#0a0c10]/60 text-[#E8E0D0]/25 border border-transparent'
              }`}>
              {f.toUpperCase()}
            </button>
          ))}
          <span className="font-['Outfit'] text-[#E8E0D0]/15 text-[7px] ml-auto self-center bg-[#0a0c10]/60 px-1.5 py-0.5 rounded-full">{filtered.length}</span>
        </div>
      </div>

      {/* ACTION RESULT TOAST */}
      <AnimatePresence>
        {actionResult && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`absolute top-28 left-1/2 -translate-x-1/2 z-[1001] px-4 py-2 rounded-lg backdrop-blur-sm ${
              actionResult.type === 'error' ? 'bg-[#8B1A1A]/80 border border-[#8B1A1A]' :
              actionResult.type === 'fail' ? 'bg-[#C9A84C]/80 border border-[#C9A84C]' :
              'bg-[#1a3a1a]/80 border border-[#4ADE80]'
            }`}>
            <span className="font-['Outfit'] text-xs text-white">{actionResult.msg}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LEAFLET MAP */}
      <MapContainer
        center={NYC_CENTER}
        zoom={NYC_ZOOM}
        minZoom={2}
        maxBounds={[[-85, -180], [85, 180]]}
        maxBoundsViscosity={1.0}
        worldCopyJump={false}
        style={{ height: '100%', width: '100%', background: '#080C10' }}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          url={`https://{s}.basemaps.cartocdn.com/rastertiles/dark_all/{z}/{x}/{y}{r}.png${process.env.REACT_APP_CARTO_KEY ? '?key=' + process.env.REACT_APP_CARTO_KEY : ''}`}
          maxZoom={18}
          opacity={0.7}
          noWrap={true}
        />
        <ZoomTracker onZoomChange={setZoom} />
        {flyTarget && <FlyTo center={flyTarget.center} zoom={flyTarget.zoom} />}

        {/* Territory markers */}
        {filtered.map(t => {
          const colors = getBlockColors(t, gameId);
          const r = getRadius(t.income_per_cycle, zoom);
          const isSelected = selectedTerritory?.territory_id === t.territory_id;
          const hasStory = !!t.story_marker;

          return (
            <CircleMarker
              key={t.territory_id}
              center={[t.lat, t.lng]}
              radius={isSelected ? r * 1.4 : r}
              pathOptions={{
                fillColor: colors.fill,
                fillOpacity: isSelected ? 0.95 : hasStory ? 0.85 : 0.7,
                color: isSelected ? '#FFD700' : colors.stroke,
                weight: isSelected ? 2.5 : t.premium ? 2 : 1,
              }}
              eventHandlers={{
                click: () => setSelectedTerritory(t),
              }}
            >
              {zoom >= 14 && (
                <Popup className="kow-popup" closeButton={false} autoPan={false}>
                  <div className="text-center" style={{ minWidth: '100px' }}>
                    <p className="font-bold text-xs" style={{ color: colors.fill }}>{t.kow_name}</p>
                    <p className="text-[10px] text-gray-400">${t.income_per_cycle?.toLocaleString()}/cycle</p>
                    {t.owner_name && <p className="text-[10px] text-gray-300">{t.owner_name}</p>}
                  </div>
                </Popup>
              )}
            </CircleMarker>
          );
        })}
      </MapContainer>

      {/* WAR FEED — floating bottom-left */}
      {warFeed.length > 0 && !selectedTerritory && (
        <div className="absolute bottom-2 left-2 z-[1000] max-w-xs">
          <div className="bg-[#0a0c10]/85 backdrop-blur-sm border border-[#1A2535] rounded-lg p-2 space-y-1">
            <p className="font-['Outfit'] text-[#8B1A1A]/50 text-[7px] tracking-widest">WAR FEED</p>
            {warFeed.slice(0, 4).map((e, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[8px] font-['Outfit']">
                <span className={e.action === 'captured' ? 'text-[#8B1A1A]' : e.action === 'defended' ? 'text-[#4ADE80]' : 'text-[#C9A84C]'}>
                  {e.action === 'captured' ? <Sword size={8} /> : e.action === 'defended' ? <Shield size={8} /> : <MapPin size={8} />}
                </span>
                <span className="text-[#E8E0D0]/40 truncate">
                  <span className="text-[#E8E0D0]/60">{e.actor_name}</span>
                  {' '}{e.action}{' '}
                  <span className="text-[#C9A84C]/60">{e.territory_id}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* LEGEND — floating bottom-right */}
      {!selectedTerritory && (
        <div className="absolute bottom-2 right-2 z-[1000]">
          <div className="bg-[#0a0c10]/85 backdrop-blur-sm border border-[#1A2535] rounded-lg p-2 space-y-0.5">
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#C9A84C]" /><span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/40">YOUR TERRITORY</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#8B1A1A]" /><span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/40">ENEMY</span></div>
            <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-[#3a3a3a]" /><span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/40">NEUTRAL</span></div>
            <div className="flex items-center gap-1.5"><Skull size={8} className="text-[#ff4444]" /><span className="font-['Outfit'] text-[7px] text-[#E8E0D0]/40">STORY MARKER</span></div>
          </div>
        </div>
      )}

      {/* TERRITORY DETAIL — Bottom sheet */}
      <AnimatePresence>
        {selectedTerritory && (
          <TerritoryDetail
            territory={selectedTerritory}
            myGameId={gameId}
            onClaim={handleClaim}
            onAttack={handleAttack}
            onReinforce={handleReinforce}
            onClose={() => setSelectedTerritory(null)}
            loading={actionLoading}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultiplayerMap;
